import { DF_COLLAPSE_EXPAND } from '@lib/fresco';
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import {
  SkillScaleLevelChangeModalTypes,
  SkillScaleLevelChangeService,
} from '@app/profile/components/skill-scale-level-change-modal/services/skill-scale-level-change.service';
import {
  Profile,
  UserProfileSkillPermissions,
} from '@app/profile/profile.model';
import { ProfileSkillsService } from '@app/profile/services/profile-skills.service';
import { ProfileService } from '@app/profile/services/profile.service';
import { ColumnConfiguration } from '@app/shared/components/column-configurator/column-configurator.model';
import { PillOption } from '@app/shared/components/pill-selector/pill-selector.model';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { NotifierService } from '@app/shared/services/notifier.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { UserPreferencesService } from '@app/shared/services/user-preferences/user-preferences.service';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { camelCaseKeys } from '@app/shared/utils/property';
import { SkillStandardsService } from '@app/skill-standards/services/skill-standards.service';
import { SkillsStandardsApi } from '@app/skill-standards/skill-standards-api.model';
import { TagTileComponent } from '@app/tags/components/tag-tile/tag-tile.component';
import { TagModalService } from '@app/tags/services/tag-modal.service';
import { TagRatingTrackerService } from '@app/tags/services/tag-rating-tracker.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { NgbDropdownConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, once } from 'lodash-es';
import { combineLatest, Observable, of, take } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ProfileFilter } from '../profile-filter-bar/profile-filter-bar.component';
import {
  INITIAL_PAGINATION_PAGE,
  INITIAL_PAGINATION_PER_PAGE,
} from '../profile-skills-list/profile-skills-list.component';
import { LDFlagsService } from '@dg/shared-services';

interface SortedTags {
  other: TagsApi.TagDetails[];
  required: TagsApi.TagDetails[];
  focus: TagsApi.TagDetails[];
}

export interface FilteredTag extends TagsApi.Tag {
  show: boolean;
}

export const LOCAL_STORAGE_PROFILE_SKILLS_LIST_CONFIG =
  'profileSkillsListConfiguration';
export const LOCAL_STORAGE_PROFILE_SKILLS_LIST_LAYOUT =
  'profileSkillsListLayout';

/*
 * ************************************************************************************
 * Please consider the `list` view (profile-skills-list.component.ts) when making changes here.
 * - Will any of your changes cause undesirable side effects in the list view (think data changes: search, sort etc)?
 * - Do you need to update the list view to keep business logic in sync?
 * ************************************************************************************
 */
@Component({
  selector: 'dgx-profile-skills',
  templateUrl: './profile-skills.component.html',
  styleUrls: ['./profile-skills.component.scss'],
  providers: [NgbDropdownConfig],
  animations: [DF_COLLAPSE_EXPAND],
})
export class ProfileSkillsComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public readonly CLASS_NAME = 'ProfileSkillsComponent';

  @Input() public owner: Profile;
  @Input() public hasSkillsCheckup: boolean;
  @Input() public isSkillInventoryProfile: boolean;
  @Input() public isSkillAnalyticsProfile: boolean;
  @Input() public showSignalsColumn: boolean;

  @ViewChildren(TagTileComponent)
  public renderedTags: QueryList<TagTileComponent>;
  @ViewChild('focusSkillContainer')
  public focusSkillContainer: ElementRef<HTMLElement>;
  @ViewChild('additionalSkillContainer')
  public additionalSkillContainer: ElementRef<HTMLElement>;

  public readonly maxFocusSkills = 4;
  public readonly MAX_SELECTABLE_COLUMNS = 6;
  public readonly PROFILE_SKILLS_TABLE_DEFAULT_HIERARCHY = [
    InternalTagRatingTypes.self,
    InternalTagRatingTypes.peer,
    InternalTagRatingTypes.manager,
    InternalTagRatingTypes.evaluation,
    InternalTagRatingTypes.total,
    InternalTagRatingTypes.signals,
    InternalTagRatingTypes.target,
    InternalTagRatingTypes.credential,
  ];

  public i18n = this.translateService.instant([
    'CredNav_SkillReview',
    'dgProfileOverview_RequiredCriticalSkills',
    'dgProfileOverview_RequiredSkillsDescription',
    'dgProfileOverview_AdditionalSkills',
    'dgProfileOverview_AddSkillOrTopic',
    'dgProfileOverview_FocusSkills',
    'dgProfileOverview_FocusSkillsDescription',
    'dgProfileOverview_FocusSkillsDescriptionOtherProfile',
    'dgProfileOverview_FilterForSkills',
    'dgTagRating_RatingType',
    'Core_NoResultsFor',
    'Core_Empty_Image_Alt',
    'dgTagRating_SelfAssessment',
    'dgTagRating_ManagerAssessment',
    'dgTagRating_Evaluation',
    'dgTagRating_SkillCertification',
    'PersonalSkillView_SkillTarget',
    'Ext_TopicAddedSingle',
    'UserController_NoResults',
  ]);

  public layoutOptions: PillOption[] = [
    {
      id: 'grid',
      icon: 'grid',
      label: this.translateService.instant('Core_Grid'),
      ariaLabel: this.translateService.instant('Core_Grid'),
    },
    {
      id: 'list',
      icon: 'bulleted-list',
      label: this.translateService.instant('Core_List'),
      ariaLabel: this.translateService.instant('Core_List'),
    },
  ];
  public selectedLayoutId: string = this.layoutOptions[0].id;
  public noItemsImage = this.getEmptyImageUrl();
  public focusSkillDescriptionText =
    this.i18n.dgProfileOverview_FocusSkillsDescription;
  public additionalSkillsHeading = this.i18n.dgProfileOverview_AdditionalSkills;
  public requiredSkillsHeading =
    this.i18n.dgProfileOverview_RequiredCriticalSkills;
  public requiredSkillDescriptionText =
    this.i18n.dgProfileOverview_RequiredSkillsDescription;
  public maxFocusSkillText = this.translateService.instant(
    'dgProfileOverview_MaxFocusedSkills',
    {
      maxFocusSkills: this.maxFocusSkills,
    }
  );

  public authUser: AuthUser | null;
  public chunkRendered: boolean[] = [];
  public isOwner: boolean;
  public skillsIsEmpty: boolean;
  public skillsLoading: boolean = false;
  public sortedTags: SortedTags;
  public showSearchResults: boolean = false;
  public showFocusSkills: boolean = false;
  public showRequiredSkills: boolean = false;
  public tags: TagsApi.FilteredTag[]; // derived list of tags
  public activeFilter: ProfileFilter = {
    searchTerm: '',
    facets: [],
    isActive: false,
  };
  public filter: ProfileFilter = {
    searchTerm: '',
    facets: [],
    isActive: false,
  };
  public viewIsReady: boolean = false;

  public skillStandards: SkillsStandardsApi.SkillStandardsCard[];

  public readonly skillScaleLevelChangeModalTypes =
    SkillScaleLevelChangeModalTypes;

  // Column configuration
  public columnConfigurations: ColumnConfiguration[]; // stores the current column config
  public preferredColumnConfigurations: ColumnConfiguration[]; // stores the column config saved preference
  public visibleColumnTypes: InternalTagRatingTypes[]; // the columns that should be visible on the table
  public availableColumns: Partial<ColumnConfiguration>[]; // the columns that should be available to the column configurator

  public showViewRatingScaleButton: boolean = false;

  // Check if we need to open a skill modal based on the query string params, but only
  // do this once, regardless of how many times this is called.
  private skillModalCheck = once(() => {
    if (this.route?.queryParams) {
      this.route.queryParams.subscribe((params) => {
        if (params.s) {
          this.openSkillModalOnLoad(params.s);
        }
        if (params.showModal) {
          this.openAnnouncementModalOnLoad(params.showModal);
        }
      });
    }
  });

  constructor(
    public tagRatingService: TagRatingService,
    private translateService: TranslateService,
    private authService: AuthService,
    private profileSkillsService: ProfileSkillsService,
    private userPreferencesService: UserPreferencesService,
    private tagModalService: TagModalService,
    private tagRatingTrackerService: TagRatingTrackerService,
    private notifierService: NotifierService,
    private renderer: Renderer2,
    private skillStandardsService: SkillStandardsService,
    private trackerService: TrackerService,
    private webEnvironmentService: WebEnvironmentService,
    config: NgbDropdownConfig,
    private route: ActivatedRoute,
    private router: Router,
    private windowLayoutService: WindowLayoutService,
    private profileService: ProfileService,
    private skillScaleLevelChangeService: SkillScaleLevelChangeService,
    private ldFlagsService: LDFlagsService
  ) {
    super();
    config.autoClose = 'outside';
  }

  public get isMobile(): boolean {
    return this.windowLayoutService.isMobile;
  }

  public get helpText() {
    const src = this.webEnvironmentService.getBlobUrl(
      '/content/svg-icons/small/star.svg'
    );

    return this.translateService.instant(
      'dgProfileOverview_FocusSkillsHelp_and_Core_Icon',
      {
        starIcon: `<img src="${src}" />`,
      }
    );
  }

  public ngOnInit(): void {
    this.trackerService.setLocation('Profile');
    this.authUser = this.authService.authUser;
    const hasSkillsPlatform = this.hasSkillsPlatform();
    const defaultLayout = hasSkillsPlatform ? 'list' : 'grid';
    this.isOwner =
      this.authUser?.viewerProfile?.userProfileKey === this.owner.id;
    if (!this.isOwner) {
      this.maxFocusSkillText = undefined;
      this.focusSkillDescriptionText = this.translateService.instant(
        'dgProfileOverview_FocusSkillsDescriptionOtherProfile',
        {
          name: this.owner.name,
        }
      );
    } else {
      this.selectedLayoutId = this.userPreferencesService.get(
        LOCAL_STORAGE_PROFILE_SKILLS_LIST_LAYOUT,
        defaultLayout
      );
    }
    this.skillsIsEmpty = false;

    if (this.hasSkillsCheckup === null || this.hasSkillsCheckup === undefined) {
      this.showSignalsColumn = this.ldFlagsService.showProfileOverviewTab;

      this.profileService
        .getUserProfileSkillsPermissions()
        .subscribe((response: UserProfileSkillPermissions) => {
          this.hasSkillsCheckup = response.hasSkillsCheckup;
          this.isSkillAnalyticsProfile = response.isSkillAnalyticsProfile;
          this.isSkillInventoryProfile = response.isSkillInventoryProfile;
        });
    }

    const canUserOptIntoTeams = this.tagRatingService
      .canUserOptIntoTeams()
      .pipe(shareReplay(1));

    let skillStandards =
      new Observable<SkillsStandardsApi.UserSkillStandardsCards>();
    skillStandards = of({ assigned: [], authoringOnly: [] });

    this.profileSkillsService
      .getUserProfileSkills(this.owner.id)
      .pipe(this.takeUntilDestroyed())
      .subscribe(({ data, reason }) => {
        this.skillsLoading = true;

        if (reason) {
          this.animateTagTileFocusToggle(reason);
        }
        combineLatest([canUserOptIntoTeams, skillStandards, data]).subscribe(
          ([
            canUserOptIntoTeams,
            skillStandards,
            { focus, required, other, filter },
          ]) => {
            focus = focus.map((focusedSkill) => ({
              ...focusedSkill,
              isFocusSkillForOwner: true,
              isOnProfileForOwner: true,
              isFocused: true,
            }));
            required = required.map((requiredSkill) => ({
              ...requiredSkill,
              isFocusSkillForOwner: false,
              isOnProfileForOwner: true,
              isFocused: false,
            }));
            other = other.map((otherSkill) => ({
              ...otherSkill,
              isFocusSkillForOwner: false,
              isOnProfileForOwner: true,
              isFocused: false,
            }));
            this.sortedTags = {
              focus,
              required,
              other,
            };

            this.tags = [
              ...this.sortedTags.focus,
              ...this.sortedTags.required,
              ...this.sortedTags.other,
            ].map((tag) => ({
              ...tag,
              show: true,
            })) /* TODO: these should be sorted by UpdatedAt */;

            this.skillStandards = skillStandards.assigned;

            // don't reset configurable columns if they already exist
            if (!this.columnConfigurations) {
              this.getProfileSkillsListConfigurableColumns(canUserOptIntoTeams);
            }

            if (this.activeFilter?.isActive) {
              this.applyFilter(this.activeFilter);
            } else {
              this.activeFilter = filter;
            }
            this.showFocusSkills =
              this.sortedTags.focus.length > 0 || this.isOwner;
            this.showRequiredSkills =
              this.sortedTags.required.length > 0 && this.isOwner;
            this.skillsLoading = false;
            this.skillModalCheck();

            // If this is not the viewers profile, unsubscribe from any further updates
            if (!this.isOwner) {
              // If there are no focus skills, don't say "Other skills" in the section
              if (this.sortedTags.focus.length === 0) {
                this.additionalSkillsHeading = '';
              }
            }
          }
        );
      });

    if (this.skillScaleLevelChangeService.enableSkillsPlatform) {
      this.skillScaleLevelChangeService
        .getUserOrganizationScaleChange()
        .pipe(take(1))
        .subscribe((ratingScaleChanges: any) => {
          if (ratingScaleChanges?.length) {
            this.showViewRatingScaleButton = true;
          }
        });
    }

    this.viewIsReady = true;

    this.trackerService.trackEventData({
      action: 'Profile Skills Viewed',
      properties: {
        UserId: this.owner.id,
        IsActiveLearner: this.owner.isEngaged,
        ProfileType: this.profileService.getProfileType(),
      },
    });
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  public doneRating(): void {
    // hide the tag rating checkup SINA message
    this.hasSkillsCheckup = false;
  }

  public updateListColumns({
    columnTypes,
    columnConfigurations,
    updatePreferences = true,
  }): void {
    this.columnConfigurations = columnConfigurations;
    this.visibleColumnTypes = columnTypes;

    if (this.isOwner && updatePreferences) {
      this.userPreferencesService.set(
        LOCAL_STORAGE_PROFILE_SKILLS_LIST_CONFIG,
        columnConfigurations
      );
      this.preferredColumnConfigurations = [...columnConfigurations];
    }
  }

  /**
   * Toggle between grid/list layout
   * @param option PillOption
   */
  public changeLayout(option: PillOption): void {
    this.tagRatingTrackerService.trackSkillsViewChange(
      option.id,
      this.tags.filter((tag) => tag.show).map((tag) => camelCaseKeys(tag)),
      {
        ...this.gatherLayoutConfigurations(option.id),
        previousRenderMethod: this.selectedLayoutId,
        // track list view initial values
        pagination: option.id === 'list' ? INITIAL_PAGINATION_PAGE : undefined,
        selectedResultsPerPageCount:
          option.id === 'list' ? INITIAL_PAGINATION_PER_PAGE : undefined,
      }
    );

    this.selectedLayoutId = option.id;
    if (this.isOwner) {
      this.userPreferencesService.set(
        LOCAL_STORAGE_PROFILE_SKILLS_LIST_LAYOUT,
        option.id
      );
    }
  }

  public addSkill(event: Event): void {
    this.trackerService.trackEventData({ action: 'Skill Adding Initiated' });

    this.tagModalService
      .openAddSuggestedTagsModal(event)
      .subscribe(() =>
        this.notifierService.showSuccess(this.i18n.Ext_TopicAddedSingle)
      );
  }

  // Animate the "departure" of the card to the other section.
  public animateTagTileFocusToggle(tag: TagsApi.TagDetails): void {
    // If this not the owners profile we do not want to animate the tag movement
    if (this.isOwner) {
      // First find the DOM element for the skill tile
      const tile = this.renderedTags.find(
        (tile) => tile.tag.tagId === tag.tagId
      );
      const element = tile?.getElementRef()?.nativeElement as HTMLElement;

      // If there's no element, there's nothing to animate
      if (!element) {
        return;
      }

      // Now we need to figure out where this tile is floating off to. When adding
      // a focus skill, that's the first stub tile or the first tile if the user
      // has a full set of focus skills. When removing, that's the all skills
      // container.
      const target: HTMLElement = tag.requestingUserIsFocused
        ? this.focusSkillContainer?.nativeElement?.querySelector(
            '.focus-skill-placeholder'
          ) ||
          this.focusSkillContainer?.nativeElement?.querySelector('dgx-tag-tile')
        : this.additionalSkillContainer?.nativeElement?.querySelector(
            'dgx-tag-tile'
          );

      let transform: string;

      if (target) {
        const r1 = element.getBoundingClientRect();
        const r2 = target.getBoundingClientRect();
        const x = r2.left - r1.left;
        const y = r2.top - r1.top;

        transform = `translate(${Math.sign(x) * 20}px, ${y > 0 ? 20 : -20}px)`;
      } else {
        transform = `translate(0, ${tag.requestingUserIsFocused ? -20 : 20}px)`;
      }

      this.renderer.setStyle(element, 'transform', 'translate(0px, 0px)');
      this.renderer.addClass(element, 'fade-out'); // also adds "transition: all"
      this.renderer.setStyle(element, 'transform', transform);
    }
  }

  /**
   * Get the stubs array
   * @param num Number of skills in section
   * @param isFocusSkill Is this a focus skill section
   */
  public getStubArray(num: number, isFocusSkill: boolean = false): undefined[] {
    // Only show the first stub for the focus skills section in mobile

    if (this.windowLayoutService.isMobile && isFocusSkill) {
      return new Array(1);
    } else if (this.windowLayoutService.isTablet && !isFocusSkill) {
      return new Array(3 - (num % 3));
    } else {
      return new Array(4 - (num % 4));
    }
  }

  /**
   * Check if section needs any stubs to fill in the row
   * @param num Number of skills in section
   */
  public needStubs(num: number): boolean {
    if (this.windowLayoutService.isMobile) {
      return false;
    } else if (this.windowLayoutService.isTablet) {
      return num % 3 !== 0;
    } else {
      return num % 4 !== 0;
    }
  }

  /**
   * Check if the focus skill section needs stubs to fill in the row.
   * This will return true if the max focus skills have not been reached and the user is not on mobile.
   * On mobile, only the first stub will show with the description if no focus skills have been selected
   * @param num Number of focus skills in the section
   */
  public focusSkillsNeedStubs(num: number): boolean {
    return this.windowLayoutService.isMobile
      ? num === 0
      : num !== this.maxFocusSkills;
  }

  /**
   * Function passed to trackBy in the ngFor loops.
   * @param index
   */
  public trackByFn(index: number): number {
    return index;
  }

  /**
   * Sets the chunky chunk as rendered. This is watched by an ngIf that then
   * actually renders the skill tile instead of the blank placeholder.
   *
   * @param index the chunk number that is about to be visible
   */
  public renderChunk(index: number): void {
    this.chunkRendered[index] = true;
    if (index > 0) {
      // Because the intersection divs are 0 height, we render the previous
      // chunk too in case the user is scrolling up.
      this.chunkRendered[index - 1] = true;
    }
  }

  /**
   * Will return true for the first title if no focus skills have been selected.
   * @param num number of focus skills
   * @param tileIdx index of the current title
   */
  public shouldShowFocusTileDescription(num: number, tileIdx: number): boolean {
    return num === 0 && tileIdx === 0;
  }

  public applyFilter(updatedFilter: ProfileFilter) {
    const { data, filter } = this.profileSkillsService.doSearch(
      this.tags,
      updatedFilter
    );

    this.tags = [...data]; // Force change detection on children w/`tags` bindings

    // track here if the list view is not shown
    if (this.selectedLayoutId === 'grid') {
      this.tagRatingTrackerService.trackSkillsViewChange(
        this.selectedLayoutId,
        this.tags.filter((tag) => tag.show).map((tag) => camelCaseKeys(tag)),
        {
          ...this.gatherLayoutConfigurations(this.selectedLayoutId),
          filter,
          previousFilter: this.filter,
        }
      );
    }

    this.filter = { ...cloneDeep(filter) }; // set filter with no side effects for analytics usage
    this.activeFilter = filter; // use the filter returned from doSearch, it has the updated counts

    this.i18n.Core_NoResultsFor = this.translateService.instant(
      'Core_NoResultsFor',
      updatedFilter
    );
    this.showSearchResults = updatedFilter.isActive;
  }

  public isSearchEmpty(): boolean {
    return !this.tags.some((skill) => skill.show);
  }

  public getEmptyImageUrl(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/emptystate/empty-search-results.svg',
      true
    );
  }

  public getProfileSkillsListConfigurableColumns(
    canUserOptIntoTeams: boolean
  ): void {
    this.availableColumns =
      this.profileSkillsService.getProfileSkillsRatingTypeColumns(
        (this.tags as any).map((tag) => camelCaseKeys(tag)),
        this.isOwner,
        canUserOptIntoTeams,
        this.showSignalsColumn
      );

    let columnConfigurations = [],
      visibleColumnTypes = [],
      storedColumnConfig;

    if (this.isOwner) {
      storedColumnConfig = this.userPreferencesService.get(
        LOCAL_STORAGE_PROFILE_SKILLS_LIST_CONFIG
      );
    }

    if (storedColumnConfig) {
      columnConfigurations = storedColumnConfig;
      visibleColumnTypes = storedColumnConfig
        .filter((column) => column.visible)
        .map((column) => column.type);
      this.preferredColumnConfigurations = [...storedColumnConfig];
    } else {
      columnConfigurations = [...this.availableColumns];
      // visibility handled by the column configurator components media listener
    }

    this.columnConfigurations = columnConfigurations;
    this.visibleColumnTypes = visibleColumnTypes;
  }

  public asNumber(value: string): number {
    const result = parseInt(value);
    return isNaN(result) ? null : result;
  }

  public openAnnouncementModalOnLoad(
    modalType: SkillScaleLevelChangeModalTypes
  ): void {
    if (
      modalType.toLowerCase() ===
      SkillScaleLevelChangeModalTypes.ORG_RATING_SCALE_CHANGE.toLowerCase()
    ) {
      this.skillScaleLevelChangeService.getSkillScaleLevelChangeModal(true);
    }
  }

  private openSkillModalOnLoad(tagName: string): void {
    const tagToOpen = this.tags.filter(
      (tag) => tagName.toLowerCase() === tag.name.toLowerCase()
    );
    if (tagToOpen.length > 0) {
      this.tagRatingService
        .openSkillModal(null, tagToOpen[0], false)
        // Add custom unsubscribe logic, this passed function will be triggered on the subscription teardown
        .add(async () => {
          await this.router.navigate([], {
            queryParams: { s: undefined },
            queryParamsHandling: 'merge',
          });
        });
    }
  }

  private gatherLayoutConfigurations(layout: string): {
    columnConfiguration: InternalTagRatingTypes[];
    filter: ProfileFilter;
  } {
    return {
      columnConfiguration:
        layout === 'list' ? this.visibleColumnTypes : undefined,
      filter: this.activeFilter,
    };
  }

  private hasSkillsPlatform() {
    return this.authUser?.defaultOrgInfo?.settings.enableSkillsPlatform;
  }
}
